"use client";
import { Button } from "@/components/ui/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import { FlameIcon, InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  EvolutionIllustration,
  ListIllustration,
  LevelsIllustration,
  RingsIllustration,
} from "./KpiIntroIllustrations";
import { LEVELS } from "./levels";

// Opens by itself the first time an MSU user sees the KPIs; afterwards it is
// reopened from the link under them
const SEEN_KEY = "sherpa:kpi-intro-seen";

type Props = {
  kpis: { title: string; summary: string }[];
  groupsSeeKpisFrom: Date;
};

export default function KpiIntroDialog({ kpis, groupsSeeKpisFrom }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      // Without storage the dialog just doesn't open by itself
    }
  }, []);

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (value) return setStep(0);
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {}
  };

  const steps = [
    {
      illustration: <RingsIllustration />,
      title: "Conocé los indicadores del MSU",
      body: (
        <p>
          En el inicio vas a encontrar {kpis.length} números del 0 al 10 que
          resumen cómo está el movimiento. Se calculan en el momento con la
          información de Sherpa, para que podamos detectar a tiempo dónde hace
          falta poner el foco.
        </p>
      ),
    },
    {
      illustration: <LevelsIllustration />,
      title: "Cómo leerlos",
      body: (
        <>
          <p>
            El anillo se llena según el puntaje y su color te dice de un vistazo
            cómo venimos:
          </p>
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {LEVELS.map((level, i) => (
              <li key={level.label} className="flex items-center gap-1.5">
                <span
                  className="inline-block size-3 rounded-full"
                  style={{ backgroundColor: level.color }}
                />
                <span className="font-semibold">{level.label}</span>
                <span className="text-muted-foreground">
                  {i === 0
                    ? `${level.min} o más`
                    : `${level.min} a ${LEVELS[i - 1].min}`}
                </span>
              </li>
            ))}
          </ul>
          <p>
            Un 10 se lleva además un{" "}
            <FlameIcon
              className="inline size-4 fill-orange-400 align-text-bottom text-orange-600"
              role="img"
              aria-label="fueguito"
            />
            .
          </p>
          <p>
            Tocá el ícono{" "}
            <InfoIcon className="inline size-4 align-text-bottom" /> de cada uno
            para saber qué indica.
          </p>
        </>
      ),
    },
    {
      illustration: <ListIllustration />,
      title: "Qué mide cada uno",
      body: (
        <ul className="max-h-48 space-y-2 overflow-y-auto text-left">
          {kpis.map((kpi) => (
            <li key={kpi.title}>
              <p className="font-semibold text-foreground">{kpi.title}</p>
              <p>{kpi.summary}</p>
            </li>
          ))}
        </ul>
      ),
    },
    {
      illustration: <EvolutionIllustration />,
      title: "Mirá cómo evolucionan",
      body: (
        <p>
          El 1° de cada mes Sherpa guarda el valor de cada indicador, para todo
          el MSU y para cada grupo. Así vamos a poder ver si mejoramos con el
          tiempo.{" "}
          {new Date() < groupsSeeKpisFrom
            ? `A partir del ${groupsSeeKpisFrom.toLocaleDateString("es-UY")} los grupos van a ver en su inicio los valores de su propio grupo.`
            : "Los grupos ven en su inicio los valores de su propio grupo."}
        </p>
      ),
    },
  ];
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        className="link text-sm"
      >
        ¿Qué son estos indicadores?
      </button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
          <div className="h-44 bg-gradient-to-br from-wosm-100 to-wosm-300">
            {current.illustration}
          </div>
          <div className="min-h-56 space-y-3 px-6 pb-2 pt-5 text-center">
            <DialogTitle className="text-balance text-xl font-bold text-wosm-default">
              {current.title}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-balance text-sm text-muted-foreground">
                {current.body}
              </div>
            </DialogDescription>
          </div>
          <div className="flex items-center justify-between gap-4 px-6 pb-6 pt-4">
            <div className="flex gap-1.5" aria-hidden="true">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-2 rounded-full transition-all ${
                    i === step ? "w-6 bg-wosm-default" : "w-2 bg-wosm-200"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                >
                  Anterior
                </Button>
              )}
              <Button
                type="button"
                className="bg-wosm-default hover:bg-wosm-dark"
                onClick={() =>
                  isLast ? handleOpenChange(false) : setStep(step + 1)
                }
              >
                {isLast ? "¡Entendido!" : "Siguiente"}
              </Button>
            </div>
          </div>
          <p className="sr-only" aria-live="polite">
            Paso {step + 1} de {steps.length}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
